import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL = 2000;
const MAX_POLLS = 15;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", record: null, tries: 0 });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", record: null, tries: 0 }); return; }
    let cancelled = false;
    let tries = 0;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", record: data, tries });
          return;
        }
        if (["failed", "expired", "cancelled"].includes(data.payment_status)) {
          setState({ status: "failed", record: data, tries });
          return;
        }
        tries += 1;
        if (tries >= MAX_POLLS) {
          setState({ status: "timeout", record: data, tries });
          return;
        }
        setTimeout(tick, POLL_INTERVAL);
      } catch {
        setState({ status: "error", record: null, tries });
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0C2E2B] grain-bg grid place-items-center p-6">
      <Card className="w-full max-w-md p-8 text-center bg-white border-2 border-[#0F3B37]">
        {state.status === "paid" ? (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-600" />
            <h1 className="font-display font-black text-3xl text-[#0C2E2B] mt-4" data-testid="payment-success-heading">Payment received.</h1>
            <p className="text-sm text-zinc-500 mt-2">
              {state.record?.purpose === "booking_deposit"
                ? "Your slot is locked. Crew will call 30 min before arrival."
                : "Order confirmed. Ships in 2-3 business days."}
            </p>
            {state.record?.ref_id && (
              <div className="mt-4 bg-zinc-50 border border-zinc-200 rounded-md p-3">
                <div className="text-[10px] font-tactical uppercase tracking-widest text-zinc-500">Confirmation</div>
                <div className="font-mono-data font-bold text-lg text-[#0C2E2B]" data-testid="payment-success-ref">{state.record.ref_id}</div>
              </div>
            )}
          </>
        ) : state.status === "polling" ? (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-[#0C2E2B] animate-spin" />
            <h1 className="font-display font-black text-2xl text-[#0C2E2B] mt-4">Confirming your payment...</h1>
            <p className="text-sm text-zinc-500 mt-2">Hang tight — usually takes a few seconds.</p>
          </>
        ) : state.status === "timeout" ? (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-orange-500" />
            <h1 className="font-display font-black text-2xl text-[#0C2E2B] mt-4">Still verifying...</h1>
            <p className="text-sm text-zinc-500 mt-2">Your payment is being processed. We'll email you the moment it clears.</p>
          </>
        ) : (
          <>
            <XCircle className="w-16 h-16 mx-auto text-red-500" />
            <h1 className="font-display font-black text-2xl text-[#0C2E2B] mt-4">We couldn't verify payment</h1>
            <p className="text-sm text-zinc-500 mt-2">Contact us at gmharvey@harveys-place.com and we'll sort it out.</p>
          </>
        )}
        <Link to="/">
          <Button data-testid="payment-back-home-btn" className="btn-kinetic rounded-md mt-6 w-full">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Harvey's Place
          </Button>
        </Link>
      </Card>
    </div>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  return (
    <div className="min-h-screen bg-[#0C2E2B] grain-bg grid place-items-center p-6">
      <Card className="w-full max-w-md p-8 text-center bg-white border-2 border-[#0F3B37]">
        <XCircle className="w-16 h-16 mx-auto text-zinc-400" />
        <h1 className="font-display font-black text-2xl text-[#0C2E2B] mt-4" data-testid="payment-cancel-heading">Payment cancelled</h1>
        <p className="text-sm text-zinc-500 mt-2">
          No worries — your {ref?.startsWith("BOOK") ? "booking slot is held for 30 min" : "cart is saved"}. You can try again anytime.
        </p>
        {ref && <div className="mt-4 font-mono-data text-xs text-zinc-500">{ref}</div>}
        <Link to="/">
          <Button data-testid="payment-back-home-btn" className="btn-kinetic rounded-md mt-6 w-full">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Harvey's Place
          </Button>
        </Link>
      </Card>
    </div>
  );
}
